import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import { buildTrackingPayload, postTrackingEvent } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";
import {
  WhoStep,
  ConcernStep,
  ContactStep,
  SuccessStep,
  type HelpFor,
  type Concern,
} from "./saint-quiz/SaintQuizSteps";

const SAINT_QUIZ_HELP_FOR_FIELD = "7Kyxj4gebfAQTtgQkLid";
const SAINT_QUIZ_CONCERN_FIELD = "zGWi7qXQhj84xyF29gip";

/**
 * Detects if the visitor likely came from a paid ad source.
 * Checks fbclid, gclid, msclkid, twclid, and utm_source/utm_medium.
 */
function isPaidTraffic(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (
    params.get("fbclid") ||
    params.get("gclid") ||
    params.get("msclkid") ||
    params.get("twclid")
  )
    return true;
  const utmSource = (params.get("utm_source") || "").toLowerCase();
  const utmMedium = (params.get("utm_medium") || "").toLowerCase();
  const paidSources = [
    "facebook",
    "fb",
    "meta",
    "instagram",
    "google",
    "adwords",
    "bing",
    "tiktok",
    "twitter",
    "linkedin",
  ];
  const paidMediums = ["cpc", "ppc", "paid", "paid_social", "paid_search"];
  return (
    paidSources.some((s) => utmSource.includes(s)) ||
    paidMediums.some((m) => utmMedium === m)
  );
}

type Step = "who" | "concern" | "contact" | "success";
const STEPS: Step[] = ["who", "concern", "contact", "success"];

const SaintQuizPopup = ({
  open: controlledOpen,
  onOpenChange,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [step, setStep] = useState<Step>("who");
  const [helpFor, setHelpFor] = useState<HelpFor>("");
  const [concern, setConcern] = useState<Concern>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting">("idle");
  const [error, setError] = useState<string | null>(null);

  const isPaid = isPaidTraffic();

  // Auto-open immediately for paid traffic (once per session)
  useEffect(() => {
    if (controlledOpen !== undefined) return;
    if (!isPaid) return;
    if (sessionStorage.getItem("saintQuizShown")) return;
    sessionStorage.setItem("saintQuizShown", "1");
    setInternalOpen(true);
  }, [controlledOpen, isPaid]);

  // Lock body scroll when open
  useEffect(() => {
    const open = controlledOpen ?? internalOpen;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [controlledOpen, internalOpen]);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const reset = useCallback(() => {
    setStep("who");
    setHelpFor("");
    setConcern("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setStatus("idle");
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(reset, 300);
  }, [setIsOpen, reset]);

  const progressPct = Math.round(
    ((STEPS.indexOf(step) + 1) / STEPS.length) * 100,
  );

  const handleSubmit = async () => {
    setError(null);
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !phone.trim() ||
      !email.trim()
    ) {
      setError("Please fill in all fields so we can reach you.");
      return;
    }
    const emailClean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (phone.replace(/[^\d]/g, "").length < 10) {
      setError("Please enter a valid phone number.");
      return;
    }
    setStatus("submitting");
    try {
      const payload = buildTrackingPayload(
        "saint-quiz-lead",
        "SAINT Quiz Lead Form",
      );
      payload.formData.first_name = firstName.trim();
      payload.formData.last_name = lastName.trim();
      payload.formData.phone = phone.trim();
      payload.formData.email = emailClean;
      payload.formLabels.first_name = "First Name";
      payload.formLabels.last_name = "Last Name";
      payload.formLabels.phone = "Phone";
      payload.formLabels.email = "Email";

      postTrackingEvent(payload, {
        customFields: {
          [SAINT_QUIZ_HELP_FOR_FIELD]: {
            value: helpFor || "Not specified",
            label: "Seeking Help For",
          },
          [SAINT_QUIZ_CONCERN_FIELD]: {
            value: concern || "Not specified",
            label: "Primary Concern",
          },
        },
      });

      trackLead("saint-quiz-lead", "SAINT Quiz Lead Form");
      trackLeadConversion("saint-quiz-lead", "SAINT Quiz Lead Form");
      await new Promise((r) => setTimeout(r, 700));
      setStep("success");
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please call us at 954-827-9793.");
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(o) => (o ? setIsOpen(true) : handleClose())}
    >
      <DialogContent className="max-h-[94vh] max-w-2xl overflow-y-auto border-0 bg-card p-0 pb-[env(safe-area-inset-bottom)] sm:max-w-lg">
        {step !== "success" && (
          <div className="h-1.5 w-full bg-muted">
            <div
              className="h-full bg-gradient-accent transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}

        <button
          onClick={handleClose}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-muted/80 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {step === "who" && (
          <WhoStep
            onSelect={(v) => {
              setHelpFor(v);
              setStep("concern");
            }}
          />
        )}

        {step === "concern" && (
          <ConcernStep
            onSelect={(v) => {
              setConcern(v);
              setStep("contact");
            }}
            onBack={() => setStep("who")}
          />
        )}

        {step === "contact" && (
          <ContactStep
            firstName={firstName}
            lastName={lastName}
            phone={phone}
            email={email}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setPhone={setPhone}
            setEmail={setEmail}
            onBack={() => setStep("concern")}
            onSubmit={handleSubmit}
            status={status}
            error={error}
          />
        )}

        {step === "success" && <SuccessStep onClose={handleClose} />}

        <DialogHeader className="sr-only">
          <DialogTitle>SAINT TMS Information Request</DialogTitle>
          <DialogDescription>
            A short quiz to understand your needs and send you information about
            SAINT TMS therapy and insurance options.
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export default SaintQuizPopup;
